import React, { useState } from 'react';
import CostSheetForm from './CostSheetForm';
import CostSheetResult from './CostSheetResult';
import { calculateFinalPrice } from './costSheetUtils';

const CostSheetComparison = ({ originalSheet, onBack }) => {
  const [newSheetData, setNewSheetData] = useState(null);
  const [newSheetCalculated, setNewSheetCalculated] = useState(null);
  const [newSheetExchangeRate, setNewSheetExchangeRate] = useState(1);

  const handleNewSheetCalculate = (data) => {
    setNewSheetData(data);
    setNewSheetCalculated({
      volume: data.volume,
      calculatedWeight: data.calculatedWeight,
      effectiveWeight: data.effectiveWeight,
      interestDays: data.interestDays,
      interestAmount: data.interestAmount,
      shippingCost: data.shippingCost
    });
    setNewSheetExchangeRate(data.exchangeRate);
  };

  const originalFinalPrice = calculateFinalPrice({
    inputCost: originalSheet.inputCost,
    shippingCost: originalSheet.shippingCost,
    interestAmount: originalSheet.interestAmount,
    margin: originalSheet.margin,
    exchangeRate: originalSheet.exchangeRate,
    currency: originalSheet.currency
  });

  const newFinalPrice = newSheetData ? calculateFinalPrice({
    inputCost: newSheetData.inputCost,
    shippingCost: newSheetCalculated.shippingCost,
    interestAmount: newSheetCalculated.interestAmount,
    margin: newSheetData.margin,
    exchangeRate: newSheetExchangeRate,
    currency: newSheetData.currency
  }) : 0;

  const priceDifference = newFinalPrice - originalFinalPrice;
  const priceDifferencePercent = originalFinalPrice > 0 
    ? ((priceDifference / originalFinalPrice) * 100).toFixed(2)
    : 0;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={onBack}
            className="p-2 rounded-md hover:bg-gray-200 transition-colors"
            title="Back"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-2xl font-bold">Cost Sheet Comparison</h1>
        </div>

        {/* Original Sheet Card (Collapsed) */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6 border border-gray-200">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-6">
              <div>
                <div className="text-sm text-gray-500">Supplier</div>
                <div className="font-semibold">{originalSheet.supplierName}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Mode</div>
                <div className="font-semibold capitalize">{originalSheet.shippingMode}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Date</div>
                <div className="font-semibold">
                  {originalSheet.createdAt?.toDate 
                    ? originalSheet.createdAt.toDate().toLocaleDateString()
                    : originalSheet.createdAt instanceof Date
                    ? originalSheet.createdAt.toLocaleDateString()
                    : new Date().toLocaleDateString()}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500">Final Price</div>
              <div className="text-xl font-bold">
                {originalFinalPrice.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })} AED
              </div>
            </div>
          </div>
        </div>

        {/* New Sheet Form */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">New Cost Sheet</h2>
          <CostSheetForm
            initialValues={{
              inputCost: originalSheet.inputCost,
              supplierName: originalSheet.supplierName,
              model: originalSheet.model,
              description: originalSheet.description,
              quantity: originalSheet.quantity,
              currency: originalSheet.currency,
              hsCode: originalSheet.hsCode,
              length: originalSheet.length,
              width: originalSheet.width,
              height: originalSheet.height,
              inputWeight: originalSheet.inputWeight,
              shippingMode: originalSheet.shippingMode,
              deliveryDays: originalSheet.deliveryDays,
              paymentDate: originalSheet.paymentDate,
              incoterm: originalSheet.incoterm,
              margin: originalSheet.margin
            }}
            onCalculate={handleNewSheetCalculate}
            showResults={false}
          />
        </div>

        {/* Comparison Results */}
        {newSheetData && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Original Result */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Original Sheet</h3>
              <CostSheetResult
                formData={originalSheet}
                calculatedValues={{
                  volume: originalSheet.volume,
                  calculatedWeight: originalSheet.calculatedWeight,
                  effectiveWeight: originalSheet.effectiveWeight,
                  interestDays: originalSheet.interestDays,
                  interestAmount: originalSheet.interestAmount,
                  shippingCost: originalSheet.shippingCost
                }}
                exchangeRate={originalSheet.exchangeRate}
              />
            </div>

            {/* New Result */}
            <div>
              <h3 className="text-lg font-semibold mb-4">New Sheet</h3>
              <CostSheetResult
                formData={newSheetData}
                calculatedValues={newSheetCalculated}
                exchangeRate={newSheetExchangeRate}
              />
            </div>
          </div>
        )}

        {/* Price Difference Summary */}
        {newSheetData && (
          <div className="bg-white rounded-lg shadow-sm p-6 border-2 border-gray-200">
            <h3 className="text-lg font-semibold mb-4">Comparison Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Original Price</div>
                <div className="text-xl font-bold">
                  {originalFinalPrice.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })} AED
                </div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">New Price</div>
                <div className="text-xl font-bold">
                  {newFinalPrice.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })} AED
                </div>
              </div>
              <div className={`text-center p-4 rounded-lg ${
                priceDifference >= 0 ? 'bg-green-50' : 'bg-red-50'
              }`}>
                <div className="text-sm text-gray-600 mb-1">Difference</div>
                <div className={`text-xl font-bold ${
                  priceDifference >= 0 ? 'text-green-700' : 'text-red-700'
                }`}>
                  {priceDifference >= 0 ? '+' : ''}
                  {priceDifference.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })} AED
                </div>
                <div className={`text-sm mt-1 ${
                  priceDifference >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  ({priceDifferencePercent >= 0 ? '+' : ''}{priceDifferencePercent}%)
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CostSheetComparison;

